import React, {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {FormattedMessage, useIntl} from "react-intl";
import debounce from "lodash.debounce";

import {Alert, Badge, Button, Checkbox, Menu, MenuItem, Tab, Tabs, TextField} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";

import StyledCard from "components/card/StyledCard";
import BeatLoaderRegular from "components/loaders/BeatLoaderRegular";
import ListingPagination from "components/Product/Table/ListingPagination";

import {listConversations} from "api/personalShopper";

import {greyishBlue100, greyishBlue600} from "assets/jss/main";

import ConversationCard from "./ConversationCard";

const TABS = ["to_handle", "open", "converted", "all"];
const FILTERS = ["held", "escalated", "expiring", "suspended", "erasureRequested"];

export const INITIAL_QUERY = {tab: "to_handle", search: "", filters: {}, page: 1};

ConversationListPane.propTypes = {
    selectedId: PropTypes.number,
    memberEmail: PropTypes.string,
    query: PropTypes.shape({
        tab: PropTypes.string.isRequired,
        search: PropTypes.string.isRequired,
        filters: PropTypes.object.isRequired,
        page: PropTypes.number.isRequired,
    }).isRequired,
    onQueryChange: PropTypes.func.isRequired,
    refreshKey: PropTypes.number.isRequired,
    patches: PropTypes.object.isRequired,
    onFetched: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
};

/**
 * The left column: the tabs (to handle, open, converted), a search, the secondary filters,
 * and the conversations as cards. The query (tab, search, filters, page) lives in the
 * workspace, so it survives a narrow screen showing the conversation alone. "patches" carries
 * what the open conversation's own actions changed (a takeover, a reply), so its card follows
 * at once; "refreshKey" asks for the list again when an action changed what a tab or filter
 * holds (a suspension lifted, the last alert handled, an erasure).
 */
function ConversationListPane({selectedId, memberEmail, query, onQueryChange, refreshKey, patches, onFetched, onSelect}) {
    const intl = useIntl();
    const {tab, search, filters, page} = query;
    const [result, setResult] = useState({items: [], total: 0, pageSize: 30});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterAnchor, setFilterAnchor] = useState(null);

    const setPage = (value) => onQueryChange((previous) => ({...previous, page: value}));
    const setTab = (value) => onQueryChange((previous) => ({...previous, tab: value, page: 1}));
    const toggleFilter = (filter) => onQueryChange((previous) => ({...previous, filters: {...previous.filters, [filter]: !previous.filters[filter]}, page: 1}));
    const applySearch = useMemo(() => debounce((value) => onQueryChange((previous) => ({...previous, search: value, page: 1})), 400), [onQueryChange]);

    const activeFilters = FILTERS.filter((filter) => filters[filter]);
    const filtersKey = activeFilters.join(",");

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        listConversations({page, search, status: tab === "all" ? "" : tab, ...filters})
            .then((data) => {
                if (!cancelled) {
                    setResult(data);
                    setError(null);
                    onFetched();
                }
            })
            .catch((requestError) => !cancelled && setError(requestError))
            .finally(() => !cancelled && setLoading(false));

        return () => {
            cancelled = true;
        };
        // filtersKey stands for the filters: a new object every render, but the same keys.
    }, [page, search, tab, filtersKey, refreshKey]);

    const totalPages = Math.max(1, Math.ceil(result.total / (result.pageSize || 30)));
    const items = result.items.map((conversation) => (patches[conversation.id] ? {...conversation, ...patches[conversation.id]} : conversation));

    return (
        <StyledCard noHoverShadow style={{padding: 0, display: "flex", flexDirection: "column", minHeight: "60vh"}}>
            <Tabs
                value={tab}
                onChange={(event, value) => setTab(value)}
                variant="fullWidth"
                sx={{borderBottom: `1px solid ${greyishBlue100}`, minHeight: "40px", "& .MuiTab-root": {minHeight: "40px", textTransform: "none", fontSize: "13px", fontWeight: 600, padding: "6px 4px"}}}
            >
                {TABS.map((value) => (
                    <Tab key={value} value={value} label={<FormattedMessage id={`personalShopper.tabs.${value}`} />} />
                ))}
            </Tabs>
            <div style={{display: "flex", gap: "8px", padding: "10px 12px", alignItems: "center", borderBottom: `1px solid ${greyishBlue100}`}}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={intl.formatMessage({id: "personalShopper.list.search"})}
                    defaultValue={search}
                    onChange={(event) => applySearch(event.target.value)}
                    inputProps={{style: {fontSize: "13px"}}}
                />
                <Badge badgeContent={activeFilters.length} color="primary">
                    <Button size="small" variant="outlined" startIcon={<FilterListIcon />} onClick={(event) => setFilterAnchor(event.currentTarget)} sx={{textTransform: "none", whiteSpace: "nowrap"}}>
                        <FormattedMessage id="personalShopper.list.filters" />
                    </Button>
                </Badge>
                <Menu anchorEl={filterAnchor} open={filterAnchor !== null} onClose={() => setFilterAnchor(null)}>
                    {FILTERS.map((filter) => (
                        <MenuItem
                            key={filter}
                            dense
                            onClick={() => toggleFilter(filter)}
                        >
                            <Checkbox size="small" checked={!!filters[filter]} sx={{padding: "0 8px 0 0"}} />
                            <FormattedMessage id={`personalShopper.list.filter.${filter}`} />
                        </MenuItem>
                    ))}
                </Menu>
            </div>
            <div style={{color: greyishBlue600, fontSize: "12px", padding: "6px 12px"}}>
                <FormattedMessage id="personalShopper.list.total" values={{total: result.total}} />
            </div>
            {error && (
                <Alert severity={error.status === 404 ? "info" : "error"} sx={{margin: "0 12px 12px"}}>
                    {error.status === 404 ? <FormattedMessage id="personalShopper.notAvailable" /> : error.message}
                </Alert>
            )}
            <div style={{flex: 1, overflowY: "auto", maxHeight: "calc(100vh - 380px)"}}>
                {loading && items.length === 0 && <div style={{padding: "24px", textAlign: "center"}}><BeatLoaderRegular loading /></div>}
                {!loading && items.length === 0 && !error && (
                    <div style={{padding: "24px", textAlign: "center", color: greyishBlue600, fontSize: "13px"}}>
                        <FormattedMessage id="personalShopper.list.empty" />
                    </div>
                )}
                {items.map((conversation) => (
                    <ConversationCard
                        key={conversation.id}
                        conversation={conversation}
                        selected={conversation.id === selectedId}
                        memberEmail={memberEmail}
                        onSelect={onSelect}
                    />
                ))}
            </div>
            {totalPages > 1 && (
                <div style={{padding: "8px", borderTop: `1px solid ${greyishBlue100}`}}>
                    <ListingPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                </div>
            )}
        </StyledCard>
    );
}

export default ConversationListPane;
